/**
 * Ticker utilities: shortcode resolution, YouTube detection, etc.
 */

import { getLineupPlayerProfile, toStr } from './tickerLineupUtils';

function jerseyNumber(entry) {
    if (!entry) return null;
    const profile = getLineupPlayerProfile(entry);
    const raw = entry.num ?? profile.num ?? null;
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
}

function formatPlayerName(entry) {
    const profile = getLineupPlayerProfile(entry);
    if (!profile.surname) return null;
    return profile.prename
        ? `${profile.prename[0]}. ${profile.surname}`
        : profile.surname;
}

function teamSideForEntry(entry, homeId, awayId) {
    const tid = toStr(entry.tid?._id || entry.tid);
    if (tid === homeId) return 'h';
    if (tid === awayId) return 'a';
    return null;
}

/** Build jersey → name maps for home (h) and away (a). Lineup takes priority over roster pool. */
export function buildJerseyLookup(game, lineup = [], availablePlayers = []) {
    const lookup = { h: new Map(), a: new Map() };
    if (!game) return lookup;

    const homeId = toStr(game.hid?._id || game.hid);
    const awayId = toStr(game.aid?._id || game.aid);

    const addEntry = (entry) => {
        const side = teamSideForEntry(entry, homeId, awayId);
        const num = jerseyNumber(entry);
        const name = formatPlayerName(entry);
        if (!side || num == null || !name) return;
        lookup[side].set(num, name);
    };

    (availablePlayers || []).forEach(addEntry);
    (lineup || []).forEach(addEntry);

    return lookup;
}

/**
 * Resolve shortcodes like #h9 or #a12 to player names.
 *
 * #h9  → Heimteam, Trikotnummer 9
 * #a12 → Auswärtsteam, Trikotnummer 12
 *
 * Returns { textResolved, mentions[] }
 */
export function resolveShortcodes(text, game, availablePlayers, lineup) {
    if (!text || !game) {
        return { textResolved: text || '', mentions: [] };
    }

    const lookup = buildJerseyLookup(game, lineup, availablePlayers);
    const mentions = [];

    const resolved = text.replace(/#([ha])(\d+)/gi, (match, side, numStr) => {
        const num = parseInt(numStr, 10);
        const name = lookup[side.toLowerCase()]?.get(num);
        if (name) {
            mentions.push({
                code: match,
                name,
                teamSide: side.toLowerCase(),
                num,
            });
            return name;
        }
        return match;
    });

    return { textResolved: resolved, mentions };
}

/**
 * Extract YouTube video ID from various URL formats.
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/shorts/
 */
export function extractYouTubeId(text) {
    if (!text) return null;
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = text.match(regex);
    return match ? match[1] : null;
}

/**
 * Replace raw YouTube URLs in text with a placeholder so they don't show
 * twice (once as text, once as embed).
 */
export function stripYouTubeUrls(text) {
    if (!text) return text;
    return text
        .replace(/https?:\/\/(?:www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9_-]{11}[^\s]*/g, '')
        .replace(/https?:\/\/youtu\.be\/[a-zA-Z0-9_-]{11}[^\s]*/g, '')
        .replace(/https?:\/\/(?:www\.)?youtube\.com\/shorts\/[a-zA-Z0-9_-]{11}[^\s]*/g, '')
        .trim();
}

/** Nachspiel: minute 0 und half 0 (nur nach Abpfiff gespeichert). */
export function isPostMatchEntry(event, gameFinished = false) {
    return gameFinished && !(event.minute) && event.half === 0;
}

/** Neueste Meldung oben — unabhängig von Spielminute. */
export function sortTickerEvents(events) {
    return [...events].sort((a, b) => {
        const t = new Date(b.createdAt) - new Date(a.createdAt);
        if (t !== 0) return t;
        return (b.minute || 0) - (a.minute || 0);
    });
}
